import Column from '@components/Column';
import Row from '@components/Row';
import Text from '@components/Text';
import colors from '@constants/colors';
import styled from '@emotion/styled';
import TrashFill from '@icons/TrashFill';
import { Link } from '@stackflow/link';
import { useFlow } from '@stackflow/react';
import { pressActionEffect } from '@styles/mixin';
import { useAtomValue } from 'jotai';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import coinIcon from '../../assets/ic_coin.svg';
import transferIcon from '../../assets/ic_transfer.svg';
import trashIcon from '../../assets/ic_trash.svg';
import { currentCardAtom } from '../../atoms/prepaidCardAtoms';

const Container = styled(Column)`
    padding: 8px 16px 16px;
`;

const Info = styled(Column)``;

const Balance = styled.div`
    line-height: 1.4;
`;

const ActionSlot = styled.div`
    flex: 1 1 0;
    min-width: 0;
`;

const ChargeLink = styled(Link)`
    display: block;
    text-decoration: none;
    width: 100%;
`;

const ActionButton = styled.button`
    align-items: center;
    background: var(--color-white);
    border: 1px solid var(--color-gray2);
    border-radius: 16px;
    display: flex;
    gap: 8px;
    justify-content: center;
    min-height: 56px;
    padding: 16px 12px;
    width: 100%;

    &:disabled {
        opacity: 0.5;
    }
`;

const Icon = styled.img`
    height: 24px;
    object-fit: contain;
    width: 24px;
`;

const TransferIcons = styled.span`
    align-items: center;
    display: inline-flex;
    gap: 2px;
    height: 24px;
    justify-content: center;
    width: 24px;
`;

function CardDetail() {
    const { t } = useTranslation('prepaidCard');
    const card = useAtomValue(currentCardAtom);
    const { push } = useFlow();

    if (!card) return null;

    const canCharge = card.chargeable !== false;
    const canTransfer = card.transferable !== false;
    const canDelete = card.balance === 0;

    const handleTransfer = () => {
        if (!canTransfer) return;
        if (card.balance === 0) {
            toast.error(t('transfer.zeroBalance'));
            return;
        }
        push('PrepaidCardTransferActivity', { prepaidCardId: card.id });
    };

    const handleDelete = () => {
        push('DeleteCardModal', { prepaidCardId: card.id });
    };

    return (
        <Container fullWidth as='section' spacing={5} align='center'>
            <Info fullWidth align='center' spacing={1}>
                <Text fontWeight='medium'
                    size='title'
                    align='center'>
                    {card.name}
                </Text>
                <Balance>
                    <Text size='display' fontWeight='bold'>
                        {card.balance.toLocaleString()}{t('currencyUnit')}
                    </Text>
                </Balance>
            </Info>
            <Row fullWidth spacing={2}>
                <ActionSlot>
                    <ChargeLink activityParams={{ prepaidCardId: card.id }}
                        activityName='PrepaidCardChargeActivity'>
                        <ActionButton type='button' css={pressActionEffect} disabled={!canCharge}>
                            <Icon src={coinIcon} alt='' />
                            <Text size='body-short' fontWeight='bold' color={canCharge ? colors.textSecondary : colors.gray5}>
                                {t('actions.charge')}
                            </Text>
                        </ActionButton>
                    </ChargeLink>
                </ActionSlot>
                <ActionSlot>
                    {canDelete ? (
                        <ActionButton type='button'
                            onClick={handleDelete}
                            css={pressActionEffect}>
                            <Icon src={trashIcon} alt='' />
                            <Text size='body-short' fontWeight='bold' color={colors.textSecondary}>
                                {t('actions.delete')}
                            </Text>
                        </ActionButton>
                    ) : (
                        // 잔액 이전
                        <ActionButton type='button'
                            disabled={!canTransfer}
                            onClick={handleTransfer}
                            css={pressActionEffect}>
                            <Icon src={transferIcon} alt='' />
                            <Text size='body-short'
                                fontWeight='bold'
                                color={canTransfer ? colors.textSecondary : colors.gray5}>
                                {t('actions.transfer')}
                            </Text>
                        </ActionButton>
                    )}
                </ActionSlot>
            </Row>
        </Container>
    );
}

export default CardDetail;
